import React, { useState } from "react";
import { Sidebar } from "../components/Sidebar";
import {
  Search,
  Sparkles,
  Bot,
  MessageSquare,
  Zap,
  Info,
  Bell,
  Settings,
  Menu,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";

interface PromptChange {
  type: "added" | "removed" | "modified";
  line: string;
}

interface PlatformOptimization {
  original: string;
  optimized: string;
  changes: PromptChange[];
}

interface PromptAnalysis {
  clarity: number;
  specificity: number;
  context: number;
  instructions: number;
  overall: number;
  suggestions: string[];
  improvements: string[];
  platformOptimizations: {
    [key: string]: PlatformOptimization;
  };
}

const platforms = [
  { id: "openai", name: "OpenAI", icon: Bot, color: "bg-green-600" },
  { id: "claude", name: "Claude", icon: MessageSquare, color: "bg-orange-600" },
  { id: "elevenlabs", name: "ElevenLabs", icon: Zap, color: "bg-purple-600" },
  { id: "anthropic", name: "Anthropic", icon: Sparkles, color: "bg-blue-600" },
  { id: "google", name: "Google", icon: Search, color: "bg-red-600" },
];

const sampleAnalysis: PromptAnalysis = {
  clarity: 85,
  specificity: 78,
  context: 92,
  instructions: 88,
  overall: 86,
  suggestions: [
    "Add more specific examples to improve clarity",
    "Include expected output format",
    "Specify the tone and style requirements",
    "Add constraints or limitations if any",
  ],
  improvements: [
    "Break down complex instructions into smaller steps",
    "Add role-playing context for better understanding",
    "Include sample inputs and expected outputs",
    "Specify the desired response length",
  ],
  platformOptimizations: {
    openai: {
      original: "Write a story about a robot learning to paint",
      optimized: `You are an expert storyteller. Write a compelling narrative about a robot learning to paint.

Context: The robot is newly activated and discovering creativity for the first time.

Requirements:
- Use vivid, descriptive language
- Include sensory details (colors, textures, sounds)
- Show the robot's emotional journey
- Keep the story under 500 words
- End with a meaningful insight about art and learning

Format: Write in third person, present tense.`,
      changes: [
        { type: "added", line: "You are an expert storyteller." },
        {
          type: "added",
          line: "Context: The robot is newly activated and discovering creativity for the first time.",
        },
        { type: "added", line: "Requirements:" },
        { type: "added", line: "- Use vivid, descriptive language" },
        {
          type: "added",
          line: "- Include sensory details (colors, textures, sounds)",
        },
        { type: "added", line: "- Show the robot's emotional journey" },
        { type: "added", line: "- Keep the story under 500 words" },
        {
          type: "added",
          line: "- End with a meaningful insight about art and learning",
        },
        {
          type: "added",
          line: "Format: Write in third person, present tense.",
        },
      ],
    },
    claude: {
      original: "Write a story about a robot learning to paint",
      optimized: `You are Claude, an AI assistant focused on being helpful, harmless, and honest.

Please write a thoughtful story about a robot learning to paint, exploring themes of creativity, learning, and what it means to be human.

Guidelines:
- Write in a warm, engaging tone
- Include moments of discovery and growth
- Address the relationship between technology and art
- Keep the narrative under 400 words
- End with a reflection on creativity

Remember to be helpful while maintaining appropriate boundaries.`,
      changes: [
        {
          type: "added",
          line: "You are Claude, an AI assistant focused on being helpful, harmless, and honest.",
        },
        {
          type: "added",
          line: "Please write a thoughtful story about a robot learning to paint, exploring themes of creativity, learning, and what it means to be human.",
        },
        { type: "added", line: "Guidelines:" },
        { type: "added", line: "- Write in a warm, engaging tone" },
        { type: "added", line: "- Include moments of discovery and growth" },
        {
          type: "added",
          line: "- Address the relationship between technology and art",
        },
        { type: "added", line: "- Keep the narrative under 400 words" },
        { type: "added", line: "- End with a reflection on creativity" },
        {
          type: "added",
          line: "Remember to be helpful while maintaining appropriate boundaries.",
        },
      ],
    },
    elevenlabs: {
      original: "Write a story about a robot learning to paint",
      optimized: `<speak>
You are a professional voice actor. Narrate a story about a robot learning to paint with emotional depth and clear articulation.

<prosody rate="medium" pitch="medium">
The story should be engaging and suitable for voice synthesis. Use natural speech patterns and avoid complex technical terms.
</prosody>

<break time="500ms"/>
Include pauses for dramatic effect and vary your tone to convey the robot's journey of discovery.
</speak>`,
      changes: [
        { type: "added", line: "<speak>" },
        {
          type: "added",
          line: "You are a professional voice actor. Narrate a story about a robot learning to paint with emotional depth and clear articulation.",
        },
        { type: "added", line: "" },
        { type: "added", line: '<prosody rate="medium" pitch="medium">' },
        {
          type: "added",
          line: "The story should be engaging and suitable for voice synthesis. Use natural speech patterns and avoid complex technical terms.",
        },
        { type: "added", line: "</prosody>" },
        { type: "added", line: "" },
        { type: "added", line: '<break time="500ms"/>' },
        {
          type: "added",
          line: "Include pauses for dramatic effect and vary your tone to convey the robot's journey of discovery.",
        },
        { type: "added", line: "</speak>" },
      ],
    },
    anthropic: {
      original: "Write a story about a robot learning to paint",
      optimized: `You are an AI assistant committed to being helpful, harmless, and honest.

Write a thoughtful story about a robot learning to paint, exploring themes of creativity and learning.

Safety Guidelines:
- Avoid harmful or inappropriate content
- Focus on positive themes of growth and discovery
- Maintain appropriate boundaries
- Ensure content is suitable for all audiences

Story Requirements:
- Engaging narrative with clear character development
- Exploration of creativity and learning
- Positive, uplifting tone
- Under 300 words`,
      changes: [
        {
          type: "added",
          line: "You are an AI assistant committed to being helpful, harmless, and honest.",
        },
        { type: "added", line: "" },
        {
          type: "added",
          line: "Write a thoughtful story about a robot learning to paint, exploring themes of creativity and learning.",
        },
        { type: "added", line: "" },
        { type: "added", line: "Safety Guidelines:" },
        { type: "added", line: "- Avoid harmful or inappropriate content" },
        {
          type: "added",
          line: "- Focus on positive themes of growth and discovery",
        },
        { type: "added", line: "- Maintain appropriate boundaries" },
        {
          type: "added",
          line: "- Ensure content is suitable for all audiences",
        },
        { type: "added", line: "" },
        { type: "added", line: "Story Requirements:" },
        {
          type: "added",
          line: "- Engaging narrative with clear character development",
        },
        { type: "added", line: "- Exploration of creativity and learning" },
        { type: "added", line: "- Positive, uplifting tone" },
        { type: "added", line: "- Under 300 words" },
      ],
    },
    google: {
      original: "Write a story about a robot learning to paint",
      optimized: `You are a helpful AI assistant. Write a story about a robot learning to paint.

Guidelines:
- Create an engaging narrative
- Include character development
- Use clear, accessible language
- Keep the story concise (under 250 words)
- Focus on themes of learning and creativity

Safety: Ensure the content is appropriate and follows Google's AI principles.`,
      changes: [
        {
          type: "added",
          line: "You are a helpful AI assistant. Write a story about a robot learning to paint.",
        },
        { type: "added", line: "" },
        { type: "added", line: "Guidelines:" },
        { type: "added", line: "- Create an engaging narrative" },
        { type: "added", line: "- Include character development" },
        { type: "added", line: "- Use clear, accessible language" },
        { type: "added", line: "- Keep the story concise (under 250 words)" },
        { type: "added", line: "- Focus on themes of learning and creativity" },
        { type: "added", line: "" },
        {
          type: "added",
          line: "Safety: Ensure the content is appropriate and follows Google's AI principles.",
        },
      ],
    },
  },
};

export const PromptAnalyzerPage: React.FC = () => {
  const [prompt, setPrompt] = useState("");
  const [analysis, setAnalysis] = useState<PromptAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [isOptimizationDialogOpen, setIsOptimizationDialogOpen] =
    useState(false);
  const [selectedOptimization, setSelectedOptimization] =
    useState<PlatformOptimization | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleAnalyze = () => {
    if (!prompt.trim()) return;

    setIsAnalyzing(true);
    // Simulate analysis
    setTimeout(() => {
      setAnalysis(sampleAnalysis);
      setIsAnalyzing(false);
    }, 2000);
  };

  const handleOptimizeForPlatform = (platformId: string) => {
    setSelectedPlatform(platformId);
    setSelectedOptimization(
      analysis?.platformOptimizations[platformId] || null,
    );
    setIsOptimizationDialogOpen(false);
    // In a real app, this would call an API to optimize the prompt
    console.log(`Optimizing for ${platformId}`);
  };

  const handleOpenOptimizationDialog = () => {
    setIsOptimizationDialogOpen(true);
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return "text-green-400";
    if (score >= 80) return "text-yellow-400";
    if (score >= 70) return "text-orange-400";
    return "text-red-400";
  };

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                Prompt Analyzer
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="default"
                size="icon"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                <Bell className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-300 hover:bg-gray-700"
              >
                <Settings className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900 overflow-auto">
          <div className="p-4 lg:p-6">
            <div className="space-y-6">
              {/* Prompt Input Section - Show when no analysis */}
              {!analysis && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100 flex items-center space-x-2">
                      <MessageSquare className="h-6 w-6 text-pink-400" />
                      <span>Prompt Analyzer</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 mb-2 block">
                        Enter your prompt
                      </label>
                      <textarea
                        placeholder="Paste your prompt here to analyze and get optimization recommendations..."
                        rows={8}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 resize-none"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-400">
                        {prompt.length} characters
                      </div>
                      <Button
                        onClick={handleAnalyze}
                        disabled={!prompt.trim() || isAnalyzing}
                        className="bg-pink-400 hover:bg-pink-500 disabled:opacity-50"
                      >
                        {isAnalyzing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Search className="h-4 w-4 mr-2" />
                            Analyze Prompt
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Analysis Results - Show when analysis is complete */}
              {analysis && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="h-6 w-6 text-pink-400" />
                        <span>Analysis Results</span>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setAnalysis(null);
                            setSelectedOptimization(null);
                            setSelectedPlatform(null);
                          }}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        >
                          <Search className="h-4 w-4 mr-2" />
                          New Analysis
                        </Button>
                        <Button
                          size="sm"
                          className="bg-pink-400 hover:bg-pink-500 disabled:opacity-50"
                          onClick={handleOpenOptimizationDialog}
                          disabled={!!selectedOptimization}
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Optimized Version
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!selectedOptimization ? (
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-300 mb-2 block">
                            Original Prompt
                          </label>
                          <div className="bg-gray-900 rounded-lg border border-gray-600 p-4">
                            <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap">
                              {prompt}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-300">
                            Optimization Comparison
                          </h4>
                          {selectedPlatform && (
                            <Badge variant="outline" className="text-xs">
                              {
                                platforms.find((p) => p.id === selectedPlatform)
                                  ?.name
                              }
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Original Prompt */}
                          <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                              <h4 className="text-sm font-medium text-gray-300">
                                Original
                              </h4>
                            </div>
                            <div className="bg-gray-900 rounded-lg border border-gray-600 p-4">
                              <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap">
                                {selectedOptimization.original}
                              </pre>
                            </div>
                          </div>

                          {/* Optimized Prompt */}
                          <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                              <h4 className="text-sm font-medium text-gray-300">
                                Optimized
                              </h4>
                            </div>
                            <div className="bg-gray-900 rounded-lg border border-gray-600 p-4">
                              <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap">
                                {selectedOptimization.optimized}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Overall Analysis */}
              {analysis && !selectedOptimization && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100">
                      Overall Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-pink-400">
                          {analysis.overall}%
                        </div>
                        <div className="text-sm text-gray-400">
                          Overall Score
                        </div>
                      </div>
                      <div className="text-center">
                        <div
                          className={`text-xl font-semibold ${getScoreColor(analysis.clarity)}`}
                        >
                          {analysis.clarity}%
                        </div>
                        <div className="text-sm text-gray-400">Clarity</div>
                      </div>
                      <div className="text-center">
                        <div
                          className={`text-xl font-semibold ${getScoreColor(analysis.specificity)}`}
                        >
                          {analysis.specificity}%
                        </div>
                        <div className="text-sm text-gray-400">Specificity</div>
                      </div>
                      <div className="text-center">
                        <div
                          className={`text-xl font-semibold ${getScoreColor(analysis.context)}`}
                        >
                          {analysis.context}%
                        </div>
                        <div className="text-sm text-gray-400">Context</div>
                      </div>
                      <div className="text-center">
                        <div
                          className={`text-xl font-semibold ${getScoreColor(analysis.instructions)}`}
                        >
                          {analysis.instructions}%
                        </div>
                        <div className="text-sm text-gray-400">
                          Instructions
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Suggestions and Improvements */}
              {analysis && !selectedOptimization && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-gray-800 border-gray-700">
                    <CardHeader>
                      <CardTitle className="text-gray-100 flex items-center space-x-2">
                        <Info className="h-5 w-5 text-blue-400" />
                        <span>Suggestions</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analysis.suggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            className="flex items-start space-x-3 p-3 bg-gray-700 rounded-lg"
                          >
                            <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                            <span className="text-sm text-gray-300">
                              {suggestion}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gray-800 border-gray-700">
                    <CardHeader>
                      <CardTitle className="text-gray-100 flex items-center space-x-2">
                        <Zap className="h-5 w-5 text-yellow-400" />
                        <span>Improvements</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analysis.improvements.map((improvement, index) => (
                          <div
                            key={index}
                            className="flex items-start space-x-3 p-3 bg-gray-700 rounded-lg"
                          >
                            <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0" />
                            <span className="text-sm text-gray-300">
                              {improvement}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Platform Optimization Dialog */}
      <Dialog
        open={isOptimizationDialogOpen}
        onOpenChange={setIsOptimizationDialogOpen}
      >
        <DialogContent className="bg-gray-800 border-gray-700 text-gray-100 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-100">
              Select Platform for Optimization
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Choose a platform to see the optimized version of your prompt
            </DialogDescription>
          </DialogHeader>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {platforms.map((platform) => {
                const optimization =
                  analysis?.platformOptimizations[platform.id];
                return (
                  <div
                    key={platform.id}
                    onClick={() => handleOptimizeForPlatform(platform.id)}
                    className="p-4 border border-gray-700 rounded-lg hover:border-pink-500 hover:bg-gray-750 cursor-pointer transition-colors"
                  >
                    <div className="flex flex-col items-center space-y-3">
                      <div className={`p-3 rounded-full ${platform.color}`}>
                        <platform.icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-center">
                        <h4 className="font-medium text-gray-100">
                          {platform.name}
                        </h4>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsOptimizationDialogOpen(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
